/**
 * Les notes de crédit de transition : émises en 2026 sur une facture d'avant
 * 2026. Aucune n'entre au journal des ventes ; chacune reçoit une catégorie,
 * proposée par le calcul et validée par un humain.
 *
 * Trancher, c'est choisir une catégorie et le partage de la note entre
 * ajustement de la créance d'ouverture (400000), dette famille d'ouverture
 * (460000) et correction de l'exercice courant ; s'écarter de la proposition
 * se justifie. La note elle-même garde son numéro, sa date 2026, sa facture
 * et son motif : elle n'est jamais antidatée.
 */

import { useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { messageErreur } from '../../../lib/payments/erreur';
import { proposerLaRepriseClients, validerUneNote } from '../../../lib/compta/reprise/gateway.supabase';
import {
  CATEGORIES_NC,
  CATEGORIES_VALIDABLES,
  controlerDecision,
  repartitionParDefaut,
  type CategorieNc,
  type DecisionNc,
  type NoteDeTransition,
} from '../../../lib/compta/reprise/types';
import SaisieMontant from '../components/SaisieMontant';
import { dateCourte, euros } from '../components/money';
import type { EtatDeLaReprise } from '../AdminRepriseOuverturePage';

type Filtre = 'a_trancher' | 'toutes';

export default function NotesDeTransition({ etat, apresGeste }: { etat: EtatDeLaReprise; apresGeste: () => void }) {
  const { notes, reprise } = etat;
  const [filtre, setFiltre] = useState<Filtre>('a_trancher');
  const [ouverte, setOuverte] = useState<string | null>(null);
  const [calcul, setCalcul] = useState(false);

  const aTrancher = (n: NoteDeTransition) => !n.validee || n.categorie === 'needs_review';
  const visibles = useMemo(() => (filtre === 'toutes' ? notes : notes.filter(aTrancher)), [notes, filtre]);
  const sansCategorisation = notes.some((n) => n.repriseNcId === null);
  const total = notes.reduce((t, n) => t + n.montantCents, 0);

  const calculer = async () => {
    setCalcul(true);
    try {
      await proposerLaRepriseClients(reprise.id);
      toast.success('Proposition calculée');
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e));
    } finally {
      setCalcul(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-gray-600">
          {notes.length} note(s) de transition, {euros(total)} — {notes.filter(aTrancher).length} à valider ou à trancher.
        </p>
        <div className="flex gap-1 text-sm">
          {(['a_trancher', 'toutes'] as const).map((f) => (
            <button key={f} type="button" onClick={() => setFiltre(f)}
              className={clsx('rounded-md px-3 py-1', filtre === f ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200')}>
              {f === 'a_trancher' ? 'À trancher' : 'Toutes'}
            </button>
          ))}
        </div>
      </div>

      {sansCategorisation && reprise.statut === 'brouillon' && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          Certaines notes n’ont pas encore de catégorisation dans la reprise.
          <button type="button" onClick={calculer} disabled={calcul}
            className="ml-3 inline-flex items-center gap-1 rounded bg-amber-600 px-2 py-1 text-white hover:bg-amber-700 disabled:opacity-50">
            {calcul && <Loader2 className="h-3 w-3 animate-spin" />} Calculer la proposition
          </button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-3 py-2">Note</th>
              <th className="px-3 py-2">Facture</th>
              <th className="px-3 py-2 text-right">Montant</th>
              <th className="px-3 py-2">Catégorie</th>
              <th className="px-3 py-2">État</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {visibles.length === 0 && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">Aucune note à trancher.</td></tr>
            )}
            {visibles.map((n) => (
              <LigneNote key={n.creditNoteId} note={n} ouverte={ouverte === n.creditNoteId}
                basculer={() => setOuverte(ouverte === n.creditNoteId ? null : n.creditNoteId)}
                modifiable={reprise.statut === 'brouillon' && n.repriseNcId !== null}
                repriseId={reprise.id}
                apresGeste={() => { setOuverte(null); apresGeste(); }} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LigneNote({ note, ouverte, basculer, modifiable, repriseId, apresGeste }: {
  note: NoteDeTransition; ouverte: boolean; basculer: () => void; modifiable: boolean; repriseId: string; apresGeste: () => void;
}) {
  const aRevoir = note.categorie === 'needs_review';
  return (
    <>
      <tr className={clsx(ouverte && 'bg-indigo-50/40')}>
        <td className="px-3 py-2">
          <div className="font-medium text-gray-900">{note.numero}</div>
          <div className="text-xs text-gray-500">{dateCourte(note.date)}</div>
        </td>
        <td className="px-3 py-2">
          <div>{note.factureNumero ?? '—'}</div>
          <div className="text-xs text-gray-500">
            {dateCourte(note.factureDate)}{note.factureMontantCents !== null && ` · ${euros(note.factureMontantCents)}`}
          </div>
        </td>
        <td className="px-3 py-2 text-right tabular-nums">{euros(note.montantCents)}</td>
        <td className="px-3 py-2">
          <div className={clsx(aRevoir && 'text-red-700')}>{CATEGORIES_NC[note.categorie].label}</div>
          {note.validee && note.categorie !== note.categorieProposee && (
            <div className="text-xs text-gray-500">proposé : {CATEGORIES_NC[note.categorieProposee].label}</div>
          )}
        </td>
        <td className="px-3 py-2">
          {note.validee && !aRevoir
            ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800">Validée {dateCourte(note.valideLe)}</span>
            : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800">{aRevoir ? 'À trancher' : 'Proposée'}</span>}
        </td>
        <td className="px-3 py-2 text-right">
          <button type="button" onClick={basculer} className="text-sm text-indigo-700 hover:underline">
            {ouverte ? 'Fermer' : modifiable ? (note.validee ? 'Revoir' : 'Trancher') : 'Détail'}
          </button>
        </td>
      </tr>
      {ouverte && (
        <tr className="bg-indigo-50/40">
          <td colSpan={6} className="px-3 pb-4">
            <DetailNote note={note} modifiable={modifiable} repriseId={repriseId} apresGeste={apresGeste} />
          </td>
        </tr>
      )}
    </>
  );
}

function DetailNote({ note, modifiable, repriseId, apresGeste }: {
  note: NoteDeTransition; modifiable: boolean; repriseId: string; apresGeste: () => void;
}) {
  const initiale: CategorieNc = note.categorie === 'needs_review' ? 'opening_receivable_adjustment' : note.categorie;
  const [d, setD] = useState<DecisionNc>(() => ({
    categorie: initiale,
    ...(note.categorie === 'needs_review' || (note.ajustementCents + note.detteCents + note.correctionCents) !== note.montantCents
      ? repartitionParDefaut(initiale, note.montantCents)
      : { ajustementCents: note.ajustementCents, detteCents: note.detteCents, correctionCents: note.correctionCents }),
    justification: note.justification ?? '',
  }));
  const [envoi, setEnvoi] = useState(false);
  const refus = controlerDecision(note, d);

  const valider = async () => {
    if (!note.repriseNcId) return;
    setEnvoi(true);
    try {
      await validerUneNote(note.repriseNcId, d);
      /* Les lignes 400000 et 460000 des familles dépendent de ces décisions :
         la proposition se recalcule aussitôt. Une ligne dont le calcul change
         perd sa validation, et l'historique garde l'avant et l'après. */
      await proposerLaRepriseClients(repriseId);
      toast.success(`${note.numero} validée ; proposition clients recalculée`);
      apresGeste();
    } catch (e) {
      toast.error(messageErreur(e), { duration: 8000 });
    } finally {
      setEnvoi(false);
    }
  };

  return (
    <div className="grid gap-4 pt-2 lg:grid-cols-2">
      <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <dt className="text-gray-500">Payé avant 2026</dt><dd className="tabular-nums">{euros(note.payeAvantCents)}</dd>
        <dt className="text-gray-500">Remboursé avant 2026</dt><dd className="tabular-nums">{euros(note.rembourseAvantCents)}</dd>
        <dt className="text-gray-500">Créance au 31/12/2025</dt><dd className="tabular-nums">{euros(note.creanceAvantCents)}</dd>
        <dt className="text-gray-500">Proposition</dt><dd>{CATEGORIES_NC[note.categorieProposee].label}</dd>
        {note.raison && (<><dt className="text-gray-500">Raison</dt><dd>{note.raison}</dd></>)}
        {note.blocage && (<><dt className="text-gray-500">À trancher parce que</dt><dd className="text-red-700">{note.blocage}</dd></>)}
        {note.justification && (<><dt className="text-gray-500">Justification</dt><dd>{note.justification}</dd></>)}
      </dl>

      {modifiable ? (
        <div className="space-y-3">
          <fieldset className="space-y-1">
            {CATEGORIES_VALIDABLES.map((c) => (
              <label key={c} className="flex items-start gap-2 text-sm">
                <input type="radio" name={`cat-${note.creditNoteId}`} className="mt-1" checked={d.categorie === c}
                  onChange={() => setD({ ...d, categorie: c, ...repartitionParDefaut(c, note.montantCents) })} />
                <span>
                  <span className="font-medium text-gray-900">{CATEGORIES_NC[c].label}</span>
                  <span className="block text-xs text-gray-500">{CATEGORIES_NC[c].aide}</span>
                </span>
              </label>
            ))}
          </fieldset>
          {d.categorie !== 'current_year_prior_period_correction' && (
            <div className="flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2">Ajustement 400000
                <SaisieMontant valeurCents={d.ajustementCents} className="w-28"
                  onChange={(c) => setD({ ...d, ajustementCents: c })} ariaLabel="Ajustement de la créance d’ouverture" />
              </label>
              <label className="flex items-center gap-2">Dette 460000
                <SaisieMontant valeurCents={d.detteCents} className="w-28"
                  onChange={(c) => setD({ ...d, detteCents: c })} ariaLabel="Dette famille d’ouverture" />
              </label>
            </div>
          )}
          <label className="block text-sm">
            Justification{d.categorie !== note.categorieProposee && <span className="text-red-600"> (obligatoire)</span>}
            <textarea value={d.justification} rows={2} onChange={(x) => setD({ ...d, justification: x.target.value })}
              className="mt-1 w-full rounded border border-gray-300 px-2 py-1 text-sm" />
          </label>
          {refus.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-amber-800">{refus.map((r) => <li key={r}>{r}</li>)}</ul>
          )}
          <button type="button" onClick={valider} disabled={refus.length > 0 || envoi}
            className="inline-flex items-center gap-2 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            {envoi && <Loader2 className="h-4 w-4 animate-spin" />} Valider cette décision
          </button>
        </div>
      ) : (
        <p className="text-sm text-gray-600">
          {note.repriseNcId === null
            ? 'Calculer la proposition pour catégoriser cette note.'
            : 'La reprise n’est plus en préparation : cette décision est figée.'}
        </p>
      )}
    </div>
  );
}
